import React, { useState, useEffect } from 'react';
import styles from './VragenModule.module.css';
import { Project } from '../../types';
import { QuestionDetail } from './QuestionDetail';
import { AddQuestionModal } from './AddQuestionModal';
import { EditQuestionModal } from './EditQuestionModal';
import { questionsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

type AfzenderCategorie = 'bewoner' | 'journalist' | 'stakeholder' | 'overheid' | 'ondernemer' | 'belangenorganisatie' | 'overig';

interface Question {
  _id: string;
  id?: string;
  project: string;
  question: string;
  answer?: string | {
    content: string;
    attachments?: any[];
    answeredBy?: string;
    answeredByName?: string;
    answeredAt?: string;
  };
  status: 'new' | 'in_progress' | 'answered' | 'published';
  source?: 'email' | 'phone' | 'meeting' | 'social' | 'website';
  channel?: string;
  date?: string;
  author?: {
    name?: string;
    email?: string;
    phone?: string;
  };
  askedBy?: {
    name?: string;
    email?: string;
    category?: AfzenderCategorie;
  };
  contactInfo?: string;
  tags?: string[];
  internalNotes?: string;
  publishedDate?: string;
  viewCount?: number;
  helpfulVotes?: number;
  unhelpfulVotes?: number;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

interface VragenModuleProps {
  project: Project;
}

export const VragenModule: React.FC<VragenModuleProps> = ({ project }) => {
  const { user } = useAuth();
  const toast = useToast();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [showAnswerModal, setShowAnswerModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState<'all' | Question['status']>('all');
  const [filterCategory, setFilterCategory] = useState<'all' | AfzenderCategorie>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    question: '',
    source: 'website' as Question['source'],
    author: {
      name: '',
      email: '',
      phone: ''
    },
    tags: ''
  });
  const [answerText, setAnswerText] = useState('');

  const canEdit = user?.role === 'admin' || user?.role === 'projectManager';
  const canDelete = user?.role === 'admin';

  useEffect(() => {
    fetchQuestions();
  }, [project.id]);

  const fetchQuestions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await questionsAPI.getByProject(project.id);
      setQuestions(response.questions || []);
    } catch (err: any) {
      console.error('Failed to fetch questions:', err);
      setError(err.message || 'Failed to fetch questions');
      // Fallback to mock data if API fails
      setQuestions(mockQuestions);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateQuestion = async (questionData: any) => {
    try {
      // Map status from frontend format to backend format
      let mappedStatus = questionData.status || 'open';
      if (mappedStatus === 'in_behandeling') mappedStatus = 'in_progress';
      else if (mappedStatus === 'beantwoord') mappedStatus = 'answered';
      else if (mappedStatus === 'gesloten') mappedStatus = 'closed';

      // Map the form data to the backend expected format
      const requestData: any = {
        project: project.id,
        question: questionData.question,
        status: mappedStatus,
        askedBy: {
          name: questionData.author,
          email: questionData.author
        },
        channel: questionData.source || 'Website',
        tags: questionData.tags || []
      };

      // Only add answer if provided
      if (questionData.answer) {
        requestData.answer = {
          content: questionData.answer
        };
      }

      // Add internal notes if provided
      if (questionData.notes) {
        requestData.internalNotes = [{
          note: questionData.notes,
          author: user?.id
        }];
      }

      const newQuestion = await questionsAPI.create(requestData);

      await fetchQuestions(); // Refresh the list
      setShowAddModal(false);
    } catch (err: any) {
      console.error('Failed to create question:', err);
      toast.error('Vraag toevoegen mislukt', 'Controleer of alle verplichte velden zijn ingevuld.');
    }
  };

  const handleAnswerQuestion = async () => {
    if (!selectedQuestion) return;

    try {
      const answeredQuestion = await questionsAPI.answer(selectedQuestion._id, {
        answer: answerText,
        answeredBy: user?.id
      });
      setQuestions(questions.map(q => q._id === selectedQuestion._id ? answeredQuestion : q));
      setShowAnswerModal(false);
      setSelectedQuestion(null);
      setAnswerText('');
    } catch (err: any) {
      console.error('Failed to answer question:', err);
      toast.error('Antwoord opslaan mislukt', 'Er ging iets mis bij het beantwoorden van de vraag.');
    }
  };

  const handleEditQuestion = (question: Question) => {
    setEditingQuestion(question);
    setShowEditModal(true);
  };

  const handleUpdateQuestion = async (id: string, questionData: any) => {
    try {
      // Map status from frontend format back to backend format
      let mappedStatus = questionData.status;
      if (mappedStatus === 'in_behandeling') mappedStatus = 'in_progress';
      else if (mappedStatus === 'beantwoord') mappedStatus = 'answered';
      else if (mappedStatus === 'gesloten') mappedStatus = 'closed';

      const requestData: any = {
        question: questionData.question,
        status: mappedStatus,
        publicVisible: questionData.publicVisible || false,
        askedBy: {
          name: questionData.author,
          email: questionData.authorEmail || questionData.author,
          category: questionData.authorCategory || 'overig',
        },
        channel: questionData.source || 'Website',
        tags: questionData.tags || []
      };

      // Only add answer if provided
      if (questionData.answer) {
        requestData.answer = {
          content: questionData.answer,
          answeredByName: questionData.answeredByName || '',
        };
      }

      // Add internal notes if provided
      if (questionData.notes) {
        requestData.internalNotes = [{
          note: questionData.notes,
          author: user?.id
        }];
      }

      const updatedQuestion = await questionsAPI.update(id, requestData);

      await fetchQuestions(); // Refresh the list
      setShowEditModal(false);
      setEditingQuestion(null);
    } catch (err: any) {
      console.error('Failed to update question:', err);
      toast.error('Vraag bijwerken mislukt', 'Er ging iets mis bij het bijwerken van de vraag.');
    }
  };

  const handleDeleteQuestion = async (id: string) => {
    try {
      await questionsAPI.delete(id);
      setQuestions(questions.filter(q => q._id !== id));
      setShowEditModal(false);
      setEditingQuestion(null);
    } catch (err: any) {
      console.error('Failed to delete question:', err);
      toast.error('Vraag verwijderen mislukt', 'Er ging iets mis bij het verwijderen van de vraag.');
    }
  };

  const handleExportFAQ = async () => {
    try {
      const blob = await questionsAPI.exportCSV(project.id);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `vragen-export-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export gedownload');
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Export mislukt', 'Kon de vragen niet exporteren.');
    }
  };

  const resetForm = () => {
    setFormData({
      question: '',
      source: 'website',
      author: {
        name: '',
        email: '',
        phone: ''
      },
      tags: ''
    });
  };

  // Mock data for fallback
  const mockQuestions: Question[] = [
    {
      _id: '1',
      project: project.id,
      question: 'Wat zijn de gezondheidseffecten van de windmolens op lange termijn?',
      source: 'meeting' as const,
      date: '2024-11-28',
      status: 'published' as const,
      answer: 'Onderzoek van het RIVM toont aan dat moderne windturbines die voldoen aan de geluidsnormen geen directe gezondheidseffecten hebben. We monitoren continu het geluidsniveau en passen de turbines aan indien nodig.',
      author: { name: 'Bewoner uit Delfzijl' },
      tags: ['gezondheid', 'RIVM', 'monitoring'],
      publishedDate: '2024-11-29',
      viewCount: 234
    },
    {
      _id: '2',
      project: project.id,
      question: 'Hoe wordt geluidsoverlast gemeten en gemonitord?',
      source: 'email' as const,
      date: '2024-11-30',
      status: 'answered' as const,
      answer: 'We plaatsen permanente meetstations op strategische locaties rond het windpark. Deze meten 24/7 het geluidsniveau.',
      author: { name: 'Bewoner Dorpsstraat 12', email: 'bewoner@email.nl' },
      tags: ['geluid', 'monitoring']
    },
    {
      _id: '3',
      project: project.id,
      question: 'Kunnen bewoners participeren in de energieopbrengsten?',
      source: 'phone' as const,
      date: '2024-12-02',
      status: 'in_progress' as const,
      answer: '',
      author: { name: 'NRC Handelsblad - K. Jansen', phone: '06-12345678' },
      tags: ['participatie', 'financieel'],
      internalNotes: 'Journalist wil artikel over burgerparticipatie in energieprojecten'
    },
    {
      _id: '4',
      project: project.id,
      question: 'Wat gebeurt er met de windmolens na hun levensduur?',
      source: 'social' as const,
      date: '2024-12-03',
      status: 'new' as const,
      answer: '',
      author: { name: '@concerned_citizen' },
      tags: ['duurzaamheid', 'recycling']
    }
  ];

  const filteredQuestions = questions.filter(q => {
    const matchesStatus = filterStatus === 'all' || q.status === filterStatus;

    // Categorie filter
    const qCategory = q.askedBy?.category || (q as any).author?.category || 'overig';
    const matchesCategory = filterCategory === 'all' || qCategory === filterCategory;

    // Verbeterde zoekfunctie — zoekt in alle relevante velden
    const term = searchTerm.toLowerCase();
    const matchesSearch = term === '' ||
      q.question.toLowerCase().includes(term) ||
      (typeof q.answer === 'string' ? q.answer.toLowerCase().includes(term) : false) ||
      (typeof q.answer === 'object' && q.answer?.content ? q.answer.content.toLowerCase().includes(term) : false) ||
      (q.author?.name || '').toLowerCase().includes(term) ||
      (q.askedBy?.name || '').toLowerCase().includes(term) ||
      (q.askedBy?.email || '').toLowerCase().includes(term) ||
      (q.author?.email || '').toLowerCase().includes(term) ||
      (q.channel || '').toLowerCase().includes(term) ||
      (q.tags || []).some(t => t.toLowerCase().includes(term)) ||
      (q.internalNotes || '').toLowerCase().includes(term);

    return matchesStatus && matchesSearch && matchesCategory;
  });

  const statusColors = {
    new: '#FF6B6B',
    in_progress: '#FFA500',
    answered: '#277CEA',
    published: '#4CAF50'
  };

  const sourceIcons = {
    email: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <polyline points="22,6 12,13 2,6" />
      </svg>
    ),
    phone: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
      </svg>
    ),
    meeting: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    social: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
      </svg>
    ),
    website: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
    )
  };


  const handlePublish = async (questionId: string) => {
    try {
      const updatedQuestion = await questionsAPI.update(questionId, {
        status: 'published',
        publicVisible: true,
        publishedDate: new Date().toISOString()
      });
      setQuestions(questions.map(q =>
        q._id === questionId ? updatedQuestion : q
      ));
    } catch (err) {
      console.error('Failed to publish question:', err);
    }
  };

  // Get statistics
  const stats = {
    new: questions.filter(q => q.status === 'new').length,
    inProgress: questions.filter(q => q.status === 'in_progress').length,
    answered: questions.filter(q => q.status === 'answered').length,
    published: questions.filter(q => q.status === 'published').length,
    responseRate: Math.round((questions.filter(q => q.status === 'answered' || q.status === 'published').length / questions.length) * 100)
  };

  if (selectedQuestion) {
    return (
      <QuestionDetail
        question={selectedQuestion}
        onClose={() => setSelectedQuestion(null)}
        onEditQuestion={handleEditQuestion}
        onPublish={handlePublish}
      />
    );
  }

  if (loading) {
    return (
      <div className={styles.vragenModule}>
        <div className={styles.loading}>Loading questions...</div>
      </div>
    );
  }

  return (
    <div className={styles.vragenModule}>
      <div className={styles.moduleHeader}>
        <h1>Vragen & Antwoorden</h1>
        <p className={styles.moduleDescription}>
          Centraal beheer van vragen van bewoners, journalisten en belanghebbenden
        </p>
      </div>

      {/* Filters and Search */}
      <div className={styles.controls}>
        <div className={styles.searchBar}>
          <input
            type="text"
            placeholder="Zoek in vragen en antwoorden..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>

        <div className={styles.filters}>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as any)}
            className={styles.filterSelect}
          >
            <option value="all">Alle statussen</option>
            <option value="new">Nieuw</option>
            <option value="in_progress">In behandeling</option>
            <option value="answered">Beantwoord</option>
            <option value="published">Gepubliceerd</option>
          </select>

          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value as any)}
            className={styles.filterSelect}
          >
            <option value="all">Alle afzenders</option>
            <option value="bewoner">Bewoner</option>
            <option value="journalist">Journalist</option>
            <option value="stakeholder">Stakeholder</option>
            <option value="overheid">Overheid</option>
            <option value="ondernemer">Ondernemer</option>
            <option value="belangenorganisatie">Belangenorganisatie</option>
            <option value="overig">Overig</option>
          </select>
        </div>

        <div className={styles.headerActions}>
          <button className={styles.btnSecondary} onClick={handleExportFAQ}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Exporteer FAQ
          </button>
          {canEdit && (
            <button
              className={styles.btnPrimary}
              onClick={() => setShowAddModal(true)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Nieuwe vraag
            </button>
          )}
        </div>
      </div>

      {/* Statistics */}
      <div className={styles.statistics}>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>{stats.new}</div>
            <div className={styles.statLabel}>Nieuwe vragen</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>{stats.inProgress}</div>
            <div className={styles.statLabel}>In behandeling</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>{stats.answered + stats.published}</div>
            <div className={styles.statLabel}>Beantwoord</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>{stats.responseRate}%</div>
            <div className={styles.statLabel}>Responsratio</div>
          </div>
        </div>
      </div>

      {/* Questions Grid */}
      <div className={styles.questionsGrid}>
        {filteredQuestions.map(question => (
          <div
            key={question._id}
            className={styles.questionCard}
            onClick={() => handleEditQuestion(question)}
          >
            <div className={styles.cardHeader}>
              <div className={styles.cardBadges}>
                <span
                  className={styles.statusBadge}
                  style={{ backgroundColor: statusColors[question.status] }}
                >
                  {question.status === 'new' ? 'Nieuw' :
                   question.status === 'in_progress' ? 'In behandeling' :
                   question.status === 'answered' ? 'Beantwoord' : 'Gepubliceerd'}
                </span>
                {(question.askedBy?.category || (question as any).author?.category) && (
                  <span className={styles.categoryBadge}>
                    {question.askedBy?.category || (question as any).author?.category}
                  </span>
                )}
              </div>
            </div>

            <div className={styles.cardBody}>
              <h3>{question.question}</h3>
              {question.source && (
                <div className={styles.cardMeta}>
                  <span className={styles.sourceInfo}>
                    {sourceIcons[question.source]}
                    {question.source === 'email' ? 'E-mail' :
                     question.source === 'phone' ? 'Telefoon' :
                     question.source === 'meeting' ? 'Bijeenkomst' :
                     question.source === 'social' ? 'Social Media' : 'Website'}
                  </span>
                </div>
              )}

              {question.answer && (
                <div className={styles.answerPreview}>
                  {typeof question.answer === 'string'
                    ? question.answer.substring(0, 120)
                    : (question.answer as any).content?.substring(0, 120) || ''}...
                </div>
              )}

              <div className={styles.cardTags}>
                {question.tags && question.tags.slice(0, 3).map(tag => (
                  <span key={tag} className={styles.tag}>{tag}</span>
                ))}
                {question.tags && question.tags.length > 3 && (
                  <span className={styles.tagMore}>+{question.tags.length - 3}</span>
                )}
              </div>
            </div>

            <div className={styles.cardFooter}>
              <span className={styles.date}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                {question.date ? new Date(question.date).toLocaleDateString('nl-NL') : ''}
              </span>
              {question.author?.name && (
                <span className={styles.author}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  {question.author.name}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredQuestions.length === 0 && (
        <div className={styles.emptyState}>
          <p>Geen vragen gevonden</p>
          {searchTerm && (
            <button
              className={styles.btnSecondary}
              onClick={() => {
                setSearchTerm('');
                setFilterStatus('all');
              }}
            >
              Filters wissen
            </button>
          )}
        </div>
      )}

      {/* Add Question Modal */}
      {showAddModal && (
        <AddQuestionModal
          onClose={() => setShowAddModal(false)}
          onSave={handleCreateQuestion}
        />
      )}

      {/* Edit Question Modal */}
      {showEditModal && editingQuestion && (
        <EditQuestionModal
          question={editingQuestion}
          onClose={() => {
            setShowEditModal(false);
            setEditingQuestion(null);
          }}
          onSave={handleUpdateQuestion}
          onDelete={handleDeleteQuestion}
        />
      )}
    </div>
  );
};